import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Upload } from "lucide-react";

const SUBJECT_TYPES = { client: "Client", worker: "Treballador", supplier: "Proveïdor", other: "Altres" };
const CONSENT_METHODS = {
  written_form: "Formulari escrit/signat",
  digital_form: "Formulari digital",
  verbal: "Verbal (documentat)",
  implicit: "Implícit (conducta)",
};

const BLANK = {
  subject_type: "client", subject_name: "", subject_email: "",
  purpose: "", consent_method: "written_form", consent_date: new Date().toISOString().split("T")[0],
  withdrawn: false, withdrawn_date: "", document_url: "", notes: "",
};

export default function ConsentForm({ open, onClose, item, venueId, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setForm(item ? { ...BLANK, ...item } : BLANK);
  }, [item, open]);

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const { file_url } = await hosys.integrations.Core.UploadFile({ file });
    set("document_url", file_url);
    setUploading(false);
  };

  const handleSave = async () => {
    if (!form.subject_name || !form.purpose) return;
    setSaving(true);
    if (item?.id) {
      await hosys.entities.DataConsent.update(item.id, { ...form, venue_id: venueId });
    } else {
      await hosys.entities.DataConsent.create({ ...form, venue_id: venueId });
    }
    setSaving(false);
    onSaved();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? "Editar consentiment" : "Registrar consentiment"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipus d'interessat *</Label>
              <Select value={form.subject_type} onValueChange={v => set("subject_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(SUBJECT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Forma d'obtenció</Label>
              <Select value={form.consent_method} onValueChange={v => set("consent_method", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CONSENT_METHODS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Nom de la persona *</Label>
            <Input value={form.subject_name} onChange={e => set("subject_name", e.target.value)} placeholder="Nom complet" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email (opcional)</Label>
              <Input type="email" value={form.subject_email} onChange={e => set("subject_email", e.target.value)} placeholder="email@exemple.com" />
            </div>
            <div>
              <Label>Data del consentiment *</Label>
              <Input type="date" value={form.consent_date} onChange={e => set("consent_date", e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Finalitat del consentiment *</Label>
            <Textarea value={form.purpose} onChange={e => set("purpose", e.target.value)} rows={2} placeholder="Per a quina finalitat es va obtenir el consentiment..." />
          </div>

          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
            <input type="checkbox" id="withdrawn" checked={form.withdrawn} onChange={e => set("withdrawn", e.target.checked)} className="accent-[#7B2D3F]" />
            <Label htmlFor="withdrawn" className="cursor-pointer mb-0">Consentiment retirat</Label>
            {form.withdrawn && (
              <Input type="date" value={form.withdrawn_date} onChange={e => set("withdrawn_date", e.target.value)} className="ml-auto w-40 h-8 text-sm" />
            )}
          </div>

          <div>
            <Label>Document signat (opcional)</Label>
            <div className="flex items-center gap-3 mt-1">
              <label className="flex items-center gap-2 cursor-pointer bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 transition-colors">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Pujant..." : "Adjuntar document"}
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleUpload} disabled={uploading} />
              </label>
              {form.document_url && <span className="text-xs text-emerald-600 font-medium">✓ Document pujat</span>}
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} placeholder="Observacions..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel·lar</Button>
          <Button onClick={handleSave} disabled={!form.subject_name || !form.purpose || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {item ? "Actualitzar" : "Registrar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}